import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, Clock, ThumbsUp, ExternalLink, Share2, PackageX, Tag as TagIcon, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSite } from '../context/SiteContext';
import { addSaving } from '../utils/savings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CouponCard = ({ coupon, onUpdate }) => {
  const { settings, getImageUrl, darkMode } = useSite();
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [hasVotedWorked, setHasVotedWorked] = useState(false);
  const [hasVotedNotWorked, setHasVotedNotWorked] = useState(false);
  const [hasUsedCoupon, setHasUsedCoupon] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [countdown, setCountdown] = useState(settings.button_redirect_seconds || 5);
  // Ref to the pending navigation setTimeout so we can cancel it if the user
  // returns via BFCache (Back button on mobile). Without cancelling, the timer
  // stays queued and may fire again after restore, or the popup DOM survives
  // and blocks clicks on other coupons.
  const navTimerRef = useRef(null);

  const affiliateLink = coupon.affiliate_link || settings.affiliate_link || 'https://mercadolibre.com/sec/1ppLzNT';
  const expirationDate = new Date(coupon.expiration_date);
  const isExpired = expirationDate < new Date();
  // Sold out (agotado): stock is defined and depleted. null/undefined = unlimited.
  const isSoldOut = coupon.stock !== null && coupon.stock !== undefined && coupon.stock <= 0;
  // Unavailable = expired (out of time) OR sold out. Both show the "Cupón agotado" state.
  const isUnavailable = isExpired || isSoldOut;
  
  // Parse custom tags from settings
  let customTags = [];
  try {
    customTags = JSON.parse(settings.custom_tags || '[]');
  } catch (e) {
    customTags = [];
  }

  // Calculate which tags apply to this coupon
  const getActiveTags = () => {
    const clicks = coupon.clicks || 0;
    const likes = coupon.worked_count || 0;
    const createdAt = new Date(coupon.created_at);
    const now = new Date();
    const hoursOld = (now - createdAt) / (1000 * 60 * 60); // Hours since creation
    
    return customTags.filter(tag => {
      // Check if tag is active
      if (tag.active === false) return false;
      
      // Check time-based condition (for "Nuevo" tag)
      // If tag has max_hours_old, check if coupon is newer than that
      if (tag.max_hours_old && tag.max_hours_old > 0) {
        return hoursOld <= tag.max_hours_old;
      }
      
      // Otherwise, check copies and likes
      const meetsClicks = clicks >= (tag.min_copies || 0);
      const meetsLikes = likes >= (tag.min_likes || 0);
      return meetsClicks && meetsLikes;
    }).sort((a, b) => (b.priority || 0) - (a.priority || 0));
  };

  const activeTags = getActiveTags();

  // Colors - use custom or defaults (dark mode overrides card bg and text)
  const bgColor = darkMode
    ? (settings.dark_card_bg_color || '#1e293b')
    : (coupon.custom_bg_color || settings.default_coupon_bg_color || '#ffffff');
  const discountBg = coupon.custom_discount_bg || settings.default_coupon_discount_bg || '#4f46e5';
  const buttonBg = coupon.custom_button_bg || settings.default_coupon_button_bg || '#fbbf24';
  const buttonText = coupon.custom_button_text || settings.default_coupon_button_text || '#1e293b';
  const textColor = darkMode
    ? (settings.dark_text_color || '#f1f5f9')
    : (coupon.custom_text_color || settings.default_coupon_text_color || '#1e293b');
  const minPurchaseBg = darkMode ? '#334155' : (coupon.custom_min_purchase_bg || settings.default_coupon_min_purchase_bg || '#fef3c7');
  const borderColor = coupon.custom_border_color || settings.default_coupon_border_color || '#FFD600';

  useEffect(() => {
    // Check if user has voted
    const votedWorked = JSON.parse(localStorage.getItem('votedWorked') || '{}');
    const votedNotWorked = JSON.parse(localStorage.getItem('votedNotWorked') || '{}');
    setHasVotedWorked(!!votedWorked[coupon.id]);
    setHasVotedNotWorked(!!votedNotWorked[coupon.id]);
    
    // Check if user has used this coupon
    const usedCoupons = JSON.parse(localStorage.getItem('usedCoupons') || '{}');
    setHasUsedCoupon(!!usedCoupons[coupon.id]);
  }, [coupon.id]);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      const diff = expirationDate - now;
      if (diff <= 0) {
        setTimeRemaining(null);
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining({ days, hours, minutes, seconds, totalMs: diff });
    };
    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [coupon.expiration_date]);

  useEffect(() => {
    if (showPopup && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (showPopup && countdown === 0) {
      // Close popup BEFORE navigating so the state gets committed;
      // schedule navigation on next tick to allow React to flush.
      setShowPopup(false);
      const link = affiliateLink;
      navTimerRef.current = setTimeout(() => { window.location.href = link; }, 30);
    }
  }, [showPopup, countdown, affiliateLink]);

  // Reset popup state when the page becomes visible again (bfcache restore, tab focus,
  // pageshow). Handles the bug where returning from Mercado Libre keeps showPopup=true
  // and disables the copy button.
  useEffect(() => {
    const resetPopupState = () => {
      // Cancel any pending navigation timer left over from BFCache before it fires again
      if (navTimerRef.current) {
        clearTimeout(navTimerRef.current);
        navTimerRef.current = null;
      }
      setShowPopup(false);
      setCountdown(settings.button_redirect_seconds || 5);
    };
    const onPageShow = () => resetPopupState();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') resetPopupState();
    };
    const onFocus = () => resetPopupState();
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [settings.button_redirect_seconds]);

  const isExpiringSoon = timeRemaining && timeRemaining.totalMs < 60 * 60 * 1000;
  const hasVoted = hasVotedWorked || hasVotedNotWorked;

  // Compact time formatter: returns the 2 most significant units as an array
  // so they can be stacked vertically (day on top, hour below).
  const getTimeParts = (t) => {
    if (!t) return [];
    if (t.days > 0) return [`${t.days}d`, `${t.hours}h`];
    if (t.hours > 0) return [`${t.hours}h`, `${t.minutes}m`];
    if (t.minutes > 0) return [`${t.minutes}m`, `${t.seconds}s`];
    return [`${t.seconds}s`];
  };

  // Synchronous, non-blocking clipboard copy. Uses execCommand within the user
  // gesture (works without document focus, unlike the async Clipboard API on mobile)
  // and additionally fires the async API best-effort without awaiting it.
  const copyToClipboard = (text) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text || '';
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {
      console.error('Clipboard fallback failed:', e);
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text || '').catch(() => {});
    }
  };

  const handleCopyAndRedeem = () => {
    // IMPORTANT (mobile deep-link fix): update UI state FIRST and synchronously.
    // Returning from a native app (e.g. Mercado Libre) leaves the document without
    // focus, which makes `navigator.clipboard.writeText` hang/reject. If we awaited it
    // before showing the popup, the button appeared "dead" on mobile. Showing the popup
    // first guarantees the CTA always responds regardless of clipboard state.
    setShowPopup(true);
    setCountdown(settings.button_redirect_seconds || 5);

    // Copy to clipboard (best-effort, never awaited so it can't block the flow)
    copyToClipboard(coupon.code);

    // Mark coupon as used
    const usedCoupons = JSON.parse(localStorage.getItem('usedCoupons') || '{}');
    usedCoupons[coupon.id] = { usedAt: new Date().toISOString(), code: coupon.code };
    localStorage.setItem('usedCoupons', JSON.stringify(usedCoupons));
    setHasUsedCoupon(true);

    // Track savings for "Mi ahorro" page
    addSaving(coupon);

    // Track click (fire-and-forget). Sync stock back so the card can flip to
    // "agotado" immediately once the last unit is redeemed.
    axios.post(`${API}/coupons/${coupon.id}/click`)
      .then((res) => onUpdate?.({ clicks: (coupon.clicks || 0) + 1, stock: res.data?.stock }))
      .catch((error) => console.error('Error tracking click:', error));
  };

  // Copy code only (for used coupons)
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      toast.success('¡Código copiado!');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = coupon.code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('¡Código copiado!');
    }
  };

  const handleWorked = async () => {
    if (hasVotedWorked || hasVotedNotWorked) return;
    const votedWorked = JSON.parse(localStorage.getItem('votedWorked') || '{}');
    votedWorked[coupon.id] = true;
    localStorage.setItem('votedWorked', JSON.stringify(votedWorked));
    setHasVotedWorked(true);
    toast.success(settings.msg_validation_thanks || '¡Gracias por tu voto!');
    try {
      await axios.post(`${API}/coupons/${coupon.id}/worked`);
      onUpdate?.({ worked_count: (coupon.worked_count || 0) + 1 });
    } catch (error) {
      console.error('Error tracking vote:', error);
    }
  };

  const handleNotWorked = async () => {
    if (hasVotedWorked || hasVotedNotWorked) return;
    const votedNotWorked = JSON.parse(localStorage.getItem('votedNotWorked') || '{}');
    votedNotWorked[coupon.id] = true;
    localStorage.setItem('votedNotWorked', JSON.stringify(votedNotWorked));
    setHasVotedNotWorked(true);
    toast.success(settings.msg_validation_thanks || '¡Gracias por tu voto!');
    try {
      await axios.post(`${API}/coupons/${coupon.id}/not-worked`);
      onUpdate?.({ not_worked_count: (coupon.not_worked_count || 0) + 1 });
    } catch (error) {
      console.error('Error tracking vote:', error);
    }
  };

  const handleShare = async () => {
    const siteUrl = window.location.origin;
    let message = settings.whatsapp_share_message || 
      "🎉 ¡Mira este cupón de descuento!\n\n🏷️ {title}\n💰 Código: {code}\n\n📋 Instrucciones:\n1. Copia el código: {code}\n2. Ve al link: {affiliate_link}\n3. Pega el código en la sección de cupones\n\n🔗 Más cupones en: {site_url}";
    
    message = message
      .replace(/{title}/g, coupon.title)
      .replace(/{code}/g, coupon.code)
      .replace(/{description}/g, coupon.description || '')
      .replace(/{affiliate_link}/g, affiliateLink)
      .replace(/{site_url}/g, siteUrl);

    // Try Web Share API first (works on mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: coupon.title,
          text: message,
        });
        toast.success('¡Compartido!');
        axios.post(`${API}/coupons/${coupon.id}/share`).catch(() => {});
        return;
      } catch (err) {
        // User cancelled or error, fall through to WhatsApp
        if (err.name !== 'AbortError') {
          console.log('Web Share failed, trying WhatsApp...');
        } else {
          return; // User cancelled
        }
      }
    }

    // Fallback to WhatsApp direct link
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    axios.post(`${API}/coupons/${coupon.id}/share`).catch(() => {});
  };

  const handleGoNow = () => {
    setShowPopup(false);
    const link = affiliateLink;
    navTimerRef.current = setTimeout(() => { window.location.href = link; }, 30);
  };

  // Popup Component using Portal
  const Popup = () => {
    if (!showPopup) return null;
    
    return createPortal(
      <div 
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 pb-24 sm:pb-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}
        onClick={(e) => e.target === e.currentTarget && handleGoNow()}
      >
        <div className="bg-slate-800 text-white rounded-2xl p-5 shadow-2xl max-w-sm w-full">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
              <Check className="w-5 h-5 text-white" strokeWidth={3} />
            </div>
            <span className="font-mono font-bold text-2xl tracking-wider text-canary">
              {coupon.code}
            </span>
          </div>
          
          <p className="text-base text-slate-300 text-center mb-4">
            {settings.button_redirect_message || 'Pégalo en la sección de cupones de Mercado Libre'}
          </p>
          
          <button
            onClick={handleGoNow}
            className="w-full py-3 px-4 bg-canary text-slate-900 font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-yellow-400 transition-colors active:scale-[0.98]"
          >
            <ExternalLink className="w-5 h-5" />
            Ir a Mercado Libre
            <span className="bg-slate-800 text-canary text-sm px-2.5 py-1 rounded-full font-bold ml-2">
              {countdown}s
            </span>
          </button>
        </div>
      </div>,
      document.body
    );
  };

  // ── Modern palette (mobile-first redesign) ──────────────────────────────
  const cardBg = darkMode ? bgColor : '#ffffff';
  const cardBgImage = darkMode ? 'none' : 'linear-gradient(180deg, #ffffff 0%, #eaf1ff 100%)';
  const cardBorder = darkMode ? 'rgba(148,163,184,0.16)' : '#e3e9f5';
  const cardShadow = darkMode
    ? '0 4px 16px rgba(0,0,0,0.45)'
    : '0 2px 6px rgba(37,99,235,0.10), 0 12px 26px rgba(37,99,235,0.16)';
  const chipBg = darkMode ? '#1e293b' : '#eef4ff';
  const chipText = darkMode ? '#bfdbfe' : '#1e3a8a';
  const dotColor = darkMode ? '#64748b' : '#94a3b8';
  // Neon glow (admin-configurable, separate for border and button). Legacy `neon_enabled`
  // still turns both on for older configs.
  const legacyNeon = !!settings.neon_enabled;
  const legacyColor = settings.neon_color || '#00E5FF';
  const neonBorderEnabled = (settings.neon_border_enabled || legacyNeon) && !isUnavailable;
  const neonBorderColor = settings.neon_border_color || (legacyNeon ? legacyColor : '#00E5FF');
  const neonButtonEnabled = (settings.neon_button_enabled || legacyNeon) && !isUnavailable;
  const neonButtonColor = settings.neon_button_color || (legacyNeon ? legacyColor : '#00E5FF');
  const neonPulseOn = settings.neon_pulse_enabled !== false; // default true
  const neonCardBorder = neonBorderEnabled ? neonBorderColor : cardBorder;
  const neonCardShadow = neonBorderEnabled
    ? `0 0 5px ${neonBorderColor}, 0 0 12px ${neonBorderColor}99, 0 0 22px ${neonBorderColor}55, ${cardShadow}`
    : cardShadow;
  const neonBtnShadow = neonButtonEnabled
    ? `0 0 6px ${neonButtonColor}, 0 0 14px ${neonButtonColor}99, 0 0 24px ${neonButtonColor}55`
    : '0 4px 14px rgba(0,0,0,0.10)';
  // Split title: first token is the "discount" (e.g. $100), rest is a subtitle
  const titleFirst = coupon.title.split(' ')[0];
  const titleRest = coupon.title.split(' ').slice(1).join(' ');

  return (
    <>
      <div 
        className={`group relative rounded-[20px] sm:rounded-[22px] overflow-hidden transition-all duration-200 ease-out active:scale-[0.99] sm:hover:-translate-y-0.5 ${neonBorderEnabled && isExpiringSoon && neonPulseOn ? 'neon-pulse' : ''}`}
        style={{ backgroundColor: cardBg, backgroundImage: cardBgImage, border: `${neonBorderEnabled ? '2px' : '1px'} solid ${neonCardBorder}`, boxShadow: neonCardShadow, '--neon-color': neonBorderColor }}
        data-testid={`coupon-card-${coupon.id}`}
      >
        {/* Subtle decorative layer */}
        <div
          className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full z-0"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.14), transparent 70%)' }}
        />
        <div
          className="pointer-events-none absolute top-3 right-3 w-12 h-12 z-0"
          style={{ backgroundImage: `radial-gradient(${dotColor} 1px, transparent 1px)`, backgroundSize: '8px 8px', opacity: 0.13 }}
        />

        <div className="relative z-10 p-3 sm:p-5 flex flex-col gap-2.5 sm:gap-3">
          {/* Content that greys-out when unavailable */}
          <div className={`flex flex-col gap-2.5 sm:gap-3 ${isUnavailable ? 'grayscale opacity-70' : ''}`}>
            {/* Header: discount / bank logo + name  ·  expiration */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {coupon.image_url && (
                  <img 
                    src={getImageUrl ? getImageUrl(coupon.image_url) : coupon.image_url} 
                    alt=""
                    width={44}
                    height={44}
                    fetchPriority="high"
                    decoding="async"
                    className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-contain p-1 flex-shrink-0 bg-white border border-slate-200"
                  />
                )}
                <div className="min-w-0 flex-1">
                  {coupon.image_url ? (
                    <p className="text-base sm:text-xl font-extrabold leading-tight line-clamp-2 break-words tracking-tight" style={{ color: textColor }}>
                      {coupon.title}
                    </p>
                  ) : (
                    <>
                      <p className="text-2xl sm:text-4xl font-black leading-none tracking-tight" style={{ color: textColor }}>
                        {titleFirst}
                      </p>
                      {titleRest && (
                        <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest mt-0.5 sm:mt-1 line-clamp-2 break-words" style={{ color: textColor, opacity: 0.6 }}>
                          {titleRest}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {!isExpired && timeRemaining && (
                <div 
                  className={`flex flex-col items-center justify-center flex-shrink-0 rounded-xl px-2 py-1 leading-none ${
                    isExpiringSoon
                      ? 'bg-red-50 text-red-600'
                      : (darkMode ? 'bg-slate-700/50 text-slate-300' : 'bg-slate-100 text-slate-500')
                  }`}
                  data-testid={`coupon-time-${coupon.id}`}
                >
                  <Clock className={`w-3 h-3 sm:w-3.5 sm:h-3.5 mb-0.5 ${isExpiringSoon ? 'animate-pulse' : ''}`} strokeWidth={isExpiringSoon ? 3 : 2.5} />
                  {getTimeParts(timeRemaining).map((part, i) => (
                    <span key={i} className={`text-[10px] sm:text-xs whitespace-nowrap ${isExpiringSoon ? 'font-extrabold' : 'font-bold'}`}>
                      {part}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Tags */}
            {activeTags.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {activeTags.slice(0, 2).map((tag, index) => {
                  const isNewTag = !!tag.max_hours_old;
                  return (
                    <span 
                      key={tag.id || index}
                      className="inline-flex items-center gap-1 px-2 sm:px-2.5 py-0.5 sm:py-1 text-white text-[9px] sm:text-[11px] font-bold rounded-full uppercase tracking-wide leading-none shadow-sm"
                      style={isNewTag
                        ? { backgroundImage: 'linear-gradient(135deg, #3b82f6, #06b6d4)' }
                        : { backgroundColor: tag.color || '#3b82f6' }}
                    >
                      {isNewTag
                        ? <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3" strokeWidth={2.5} />
                        : (tag.emoji && <span className="text-[10px] sm:text-[11px]">{tag.emoji}</span>)}
                      <span>{tag.text}</span>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Description */}
            {coupon.description && (
              <p className="text-[11px] sm:text-sm font-medium line-clamp-2 leading-snug opacity-80" style={{ color: textColor }}>
                {coupon.description}
              </p>
            )}

            {/* Min purchase chip */}
            {coupon.min_purchase && (
              <span 
                className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[13px] sm:text-base font-bold w-fit"
                style={{ backgroundColor: chipBg, color: chipText }}
              >
                <TagIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.4} />
                Compra mínima: ${coupon.min_purchase?.toLocaleString()}
              </span>
            )}
          </div>

          {/* Already used badge */}
          {hasUsedCoupon && !isUnavailable && (
            <div className={`flex items-center justify-center gap-1.5 py-1 sm:py-1.5 px-2.5 sm:px-3 rounded-lg w-fit ${darkMode ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-50 text-emerald-700'}`}>
              <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span className="text-[10px] sm:text-xs font-bold">
                {settings.msg_already_used || 'Ya usaste este cupón'}
              </span>
            </div>
          )}

          {/* CTA */}
          {isUnavailable ? (
            <div className="flex flex-col gap-2">
              <div
                data-testid={`coupon-soldout-${coupon.id}`}
                className="w-full h-10 sm:h-11 rounded-xl font-bold text-[11px] sm:text-sm flex items-center justify-center gap-1.5 bg-slate-100 text-slate-500 border border-slate-200"
              >
                <PackageX className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>{settings.soldout_message || 'Cupón agotado'}</span>
              </div>
              <button
                onClick={() => navigate('/ofertas')}
                data-testid={`coupon-recommend-${coupon.id}`}
                className="w-full h-11 sm:h-12 rounded-xl font-extrabold text-[12px] sm:text-base flex items-center justify-center gap-2 transition-transform duration-200 active:scale-[0.98] sm:hover:scale-[1.01]"
                style={{ backgroundColor: buttonBg, color: buttonText, backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.28), rgba(0,0,0,0.03))' }}
              >
                <TagIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>{settings.soldout_recommend_text || 'Ver ofertas disponibles'}</span>
              </button>
            </div>
          ) : (
            <button
              onClick={handleCopyAndRedeem}
              disabled={isExpired}
              data-testid={`coupon-cta-${coupon.id}`}
              className={`w-full h-11 sm:h-[52px] rounded-xl font-extrabold text-[12px] sm:text-base flex items-center justify-center gap-1.5 sm:gap-2 transition-transform duration-200 ${
                showPopup ? 'bg-emerald-500 text-white' : ''
              } ${neonButtonEnabled && isExpiringSoon && neonPulseOn ? 'neon-pulse' : ''} active:scale-[0.98] sm:hover:scale-[1.01] sm:hover:-translate-y-0.5`}
              style={!showPopup
                ? { backgroundColor: buttonBg, color: buttonText, backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.28), rgba(0,0,0,0.03))', boxShadow: neonBtnShadow, '--neon-color': neonButtonColor }
                : { boxShadow: neonBtnShadow, '--neon-color': neonButtonColor }}
            >
              {showPopup ? (
                <><Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span>{settings.button_copied_message || '¡Copiado!'}</span></>
              ) : (
                <><Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span>{settings.button_text || 'Copiar y Canjear'}</span></>
              )}
            </button>
          )}

          {/* Bottom bar: actions + stats */}
          <div className={`flex items-center justify-between pt-2.5 sm:pt-3 border-t ${darkMode ? 'border-slate-700/60' : 'border-slate-100'}`}>
            <div className="flex items-center gap-1 sm:gap-1.5">
              <button
                onClick={handleShare}
                className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg border transition-all active:scale-95 ${darkMode ? 'border-slate-700 hover:bg-slate-700/50' : 'border-slate-200 hover:bg-slate-50'}`}
                title="Compartir"
              >
                <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: textColor }} />
              </button>
              {settings.show_worked_button && (
                <button
                  onClick={handleWorked}
                  disabled={hasVoted}
                  className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg border transition-all ${
                    hasVotedWorked
                      ? (darkMode ? 'border-emerald-700 bg-emerald-900/30' : 'border-emerald-200 bg-emerald-50')
                      : hasVoted
                        ? 'opacity-40 grayscale border-transparent'
                        : (darkMode ? 'border-slate-700 hover:bg-slate-700/50 active:scale-95' : 'border-slate-200 hover:bg-slate-50 active:scale-95')
                  }`}
                  title="Me funcionó"
                >
                  <span className="text-sm sm:text-base leading-none">{settings.action_emoji_worked || '👍'}</span>
                </button>
              )}
              {settings.show_not_worked_button && (
                <button
                  onClick={handleNotWorked}
                  disabled={hasVoted}
                  className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg border transition-all ${
                    hasVotedNotWorked
                      ? (darkMode ? 'border-red-700 bg-red-900/30' : 'border-red-200 bg-red-50')
                      : hasVoted
                        ? 'opacity-40 grayscale border-transparent'
                        : (darkMode ? 'border-slate-700 hover:bg-slate-700/50 active:scale-95' : 'border-slate-200 hover:bg-slate-50 active:scale-95')
                  }`}
                  title="No me funcionó"
                >
                  <span className="text-sm sm:text-base leading-none">{settings.action_emoji_not_worked || '👎'}</span>
                </button>
              )}
            </div>

            <div className="flex items-center gap-2.5 sm:gap-3 text-[10px] sm:text-xs font-bold" style={{ color: textColor }}>
              {settings.show_validations_count && (
                <span className="flex items-center gap-1">
                  <ThumbsUp className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-500" />
                  <span>{coupon.worked_count || 0}</span>
                </span>
              )}
              {settings.show_copies_count && (
                <span className="flex items-center gap-1">
                  <Copy className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-cyan-500" />
                  <span>{coupon.clicks || 0}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <Popup />
    </>
  );
};

export default CouponCard;
